import { GoalType } from '@griply/types';
import { canGoalHaveChildren } from '@griply/utils';
import { useCreateGoalSection, useGoalById } from 'features/goals';
import { useCreateHabitAllowed } from 'features/tasks';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle16, Repeat, Section } from 'shared/assets/icons';
import { AddGoalMenuItems } from 'shared/components/connected/add-goal-menu-items';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenCreateHabitDialog } from 'shared/contexts/habit-form';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { ID } from 'shared/types/id';

export type ActionPlanContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref'
> & {
  onClose?: () => void;
  parentIds: ID[];
  id?: ID;
};

export const ActionPlanContextMenu: React.FC<ActionPlanContextMenuProps> = ({
  referenceElement,
  location,
  position,
  onClose,
  parentIds,
  id,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const goal = useGoalById(id);
  const isProject = goal?.type === GoalType.Project;
  const isCreateHabitAllowed = useCreateHabitAllowed();
  const openPremiumDialog = useOpenPremiumDialog();
  const openCreateHabitDialog = useOpenCreateHabitDialog();
  const openCreateTask = useOpenCreateTask();
  const createSection = useCreateGoalSection(id);

  const close = () => onClose?.();

  const onCreateTask = () => {
    openCreateTask({ goalId: id });
    close();
  };

  const onCreateHabit = () => {
    openCreateHabitDialog({ goalId: id });
    close();
  };

  const _openPremiumDialog = () => {
    openPremiumDialog();
    close();
  };

  const onCreateSection = () => {
    createSection();
    close();
  };

  return (
    <PopupMenu
      ref={containerRef}
      referenceElement={referenceElement}
      location={location}
      position={position}
      onOutside={close}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateTask}
            start={<Icon icon={CheckCircle16} />}
          >
            {t('context-menus.goal.action-plan.labels.add-task')}
          </PopupMenuButton>
        </PopupMenuListItem>
        {!isProject && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={
                isCreateHabitAllowed ? onCreateHabit : _openPremiumDialog
              }
              isPremium={!isCreateHabitAllowed}
              start={<Icon icon={Repeat} />}
            >
              {t('context-menus.goal.action-plan.labels.add-habit')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
      </PopupMenuList>
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onCreateSection}
            start={<Icon icon={Section} />}
          >
            {t('general.labels.new-task-section')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
      {canGoalHaveChildren(goal) && (
        <PopupMenuList>
          <AddGoalMenuItems parentIds={parentIds} onClose={close} />
        </PopupMenuList>
      )}
    </PopupMenu>
  );
};
